import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {PixKeyType} from '@gatewee/types/banking-info';
import {DomainBankingInfo, DomainSetting} from '@gatewee/types/domain-setting';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeInputV2Component} from '../../gatewee-input-v2/gatewee-input-v2.component';
import {GateweeSelectV3Component} from '../../gatewee-select-v3/gatewee-select-v3.component';

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
}

@Component({
  selector: 'banking-info-parameter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeInputV2Component,
    GateweeSelectV3Component,
  ],
  templateUrl: './banking-info-parameter.component.html',
})
export class BankingInfoParameterComponent implements OnInit {
  formGroup!: FormGroup;
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;

  readonly pixKeyTypeItems: {value: PixKeyType; label: string}[] = [
    {
      value: PixKeyType.RANDOM_KEY,
      label:
        'features.settings.parameters.bankingInfo.pixKeyType.options.RANDOM_KEY',
    },
    {
      value: PixKeyType.DOCUMENT,
      label:
        'features.settings.parameters.bankingInfo.pixKeyType.options.DOCUMENT',
    },
    {
      value: PixKeyType.PHONE,
      label:
        'features.settings.parameters.bankingInfo.pixKeyType.options.PHONE',
    },
    {
      value: PixKeyType.MAIL,
      label: 'features.settings.parameters.bankingInfo.pixKeyType.options.MAIL',
    },
  ];

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    const data = this.context.data;
    this.id = data.id;
    this.setting = data.setting;
    this.editSettingFn = data.editSettingFn;
  }

  ngOnInit(): void {
    const parsed = this.parseValue();
    this.formGroup = this.fb.group({
      pixKey: [parsed.pixKey, Validators.required],
      pixKeyType: [parsed.pixKeyType, Validators.required],
      name: [parsed.name, Validators.required],
      document: [parsed.document, Validators.required],
    });
  }

  private parseValue(): DomainBankingInfo {
    const raw = this.setting.value;
    if (!raw) {
      return {
        pixKey: '',
        pixKeyType: PixKeyType.RANDOM_KEY,
        name: '',
        document: '',
      };
    }
    try {
      const obj =
        typeof raw === 'string'
          ? (JSON.parse(raw) as DomainBankingInfo)
          : (raw as DomainBankingInfo);
      return {
        pixKey: obj.pixKey ?? '',
        pixKeyType: (obj.pixKeyType as PixKeyType) ?? PixKeyType.RANDOM_KEY,
        name: obj.name ?? '',
        document: obj.document ?? '',
      };
    } catch {
      return {
        pixKey: '',
        pixKeyType: PixKeyType.RANDOM_KEY,
        name: '',
        document: '',
      };
    }
  }

  onSubmit(): void {
    if (this.formGroup.invalid || this.submitting) {
      this.formGroup.markAllAsTouched();
      return;
    }
    this.submitting = true;
    const payload = this.formGroup.getRawValue() as DomainBankingInfo;
    const updatedSetting: RecipientSetting | DomainSetting = {
      ...this.setting,
      value: JSON.stringify(payload),
    };
    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }
}
